import { AnimatePresence, motion } from 'motion/react';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import {
  type BankAccountLevelOverride,
  type CustomRolePermissionLevel,
  type EntityLevelOverride,
  type PlatformLevelPermissions,
} from '@column/api';
import { Icon } from '@column/column-ui-kit';
import {
  Badge,
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Separator,
  Tabs,
  TabsList,
  TabsTrigger,
  cn,
} from '@column/ui';

import {
  Access,
  Mode,
  OptionsBankAccounts,
  OptionsEntities,
  OptionsOnOff,
  Permissions,
  TransfersAndAccountsFields,
} from '../types';
import { EntitiesAndAccounts } from '../useEntitiesAndAccounts';
import {
  BankAccountLevelPermissionsToLabelsMap,
  EntityLevelPermissionsToLabelsMap,
  PlatformLevelPermissionsToLabelsMap,
  buildBankAccountOverridesFromPermissionLevel,
  buildBankAccountOverridesFromPermissions,
  buildEntityOverridesFromPermissionLevel,
  buildEntityOverridesFromPermissions,
  getAccessLevel,
  getBankAccountPermissions,
  getEntityPermissions,
  setBankAccountPermissionsFromPermissionLevel,
  setBankAccountPermissionsFromPermissions,
  setEntityPermissionsFromPermissionLevel,
  setEntityPermissionsFromPermissions,
} from '../utils';
import { FormField, Loading } from '~/components';
import { useSessionStore } from '~/stores/Session';
import { BankAccountWithDetails, EntityEntity } from '~/typings/API';

import { AccessDropdown } from './AccessDropdown';
import { CustomOverridesModal } from './CustomOverridesModal';
import { PermissionSelect } from './PermissionSelect';
import { PermissionToggles } from './PermissionToggles';

interface Props {
  excludeKeys?: (keyof KeysCamelCase<PlatformLevelPermissions>)[];
  fieldPathPrefix?: string;
  forcedMode?: Mode;
  liveEntitiesAndAccounts: EntitiesAndAccounts;
  sandboxEntitiesAndAccounts: EntitiesAndAccounts;
}

// The API allows a role's platform permissions to be null. A shared object keeps memos on it stable across renders.
const NO_PERMISSIONS = {};

const getFieldPath = (field: keyof TransfersAndAccountsFields, fieldPathPrefix?: string) =>
  `${fieldPathPrefix ? `${fieldPathPrefix}.` : ''}${field}`;

const SubsectionHeading: React.FC<{ children: ReactNode }> = ({ children }) => (
  <h3 className="m-0 text-base leading-5 font-medium text-foreground">{children}</h3>
);

// Matches the ui-kit list rows this replaces: 48px tall, indented 28px per level of depth.
const ListRow: React.FC<{
  children: ReactNode;
  className?: string;
  depth?: number;
  icon?: ReactNode;
  rightDecoration?: ReactNode;
}> = ({ children, className, depth = 0, icon, rightDecoration }) => (
  <div
    className={cn(
      'flex min-h-12 items-center gap-4 border-b border-gray-150 py-2 pr-(--page-margin) text-sm leading-4 dark:border-white/10',
      className
    )}
    style={{ paddingLeft: depth ? `calc(var(--page-margin) + ${depth * 28}px)` : 'var(--page-margin)' }}
  >
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {icon}
      {children}
    </div>
    {rightDecoration && <div className="flex shrink-0 items-center gap-4">{rightDecoration}</div>}
  </div>
);

const AccountActions: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex w-[200px] items-center justify-between gap-2.5">{children}</div>
);

const LoadMoreButton: React.FC<{ children: ReactNode; onClick: () => void }> = ({ children, onClick }) => (
  <Button type="button" size="xs" variant="subtle-primary" className="px-0" onClick={onClick}>
    {children}
  </Button>
);

const OverridesBadge: React.FC<{ count: number }> = ({ count }) => (
  <Badge variant="secondary" className="tabular-nums">
    {count === 1 ? '1 Override' : `${count} Overrides`}
  </Badge>
);

export const TransfersAndAccounts: React.FC<Props> = ({
  excludeKeys = [],
  fieldPathPrefix,
  forcedMode,
  liveEntitiesAndAccounts,
  sandboxEntitiesAndAccounts,
}) => {
  const { currentPlatform } = useSessionStore();
  const { control, setValue } = useFormContext();
  const [bankAccountSearch, setBankAccountSearch] = useState<string>('');
  const [mode, setMode] = useState<Mode>(forcedMode ?? (currentPlatform?.isLiveEnabled ? Mode.Live : Mode.Sandbox));
  const platformLevelPermissionsToLabelsMap = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(PlatformLevelPermissionsToLabelsMap).filter(
          ([permission]) => !excludeKeys.includes(permission as keyof KeysCamelCase<PlatformLevelPermissions>)
        )
      ) as typeof PlatformLevelPermissionsToLabelsMap,
    [excludeKeys]
  );
  const entityLevelPermissionsToLabelsMap = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(EntityLevelPermissionsToLabelsMap).filter(
          ([permission]) => !excludeKeys.includes(permission as keyof KeysCamelCase<PlatformLevelPermissions>)
        )
      ) as typeof EntityLevelPermissionsToLabelsMap,
    [excludeKeys]
  );
  const bankAccountLevelPermissionsToLabelsMap = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(BankAccountLevelPermissionsToLabelsMap).filter(
          ([permission]) => !excludeKeys.includes(permission as keyof KeysCamelCase<PlatformLevelPermissions>)
        )
      ) as typeof BankAccountLevelPermissionsToLabelsMap,
    [excludeKeys]
  );
  const showApproveTransfers = !excludeKeys.includes('approveTransfers');

  useEffect(() => {
    if (forcedMode) {
      setMode(forcedMode);
    }
  }, [forcedMode]);

  useEffect(() => {
    if (!bankAccountSearch) return;
    if (mode === Mode.Sandbox) {
      sandboxEntitiesAndAccounts.bankAccounts.search(bankAccountSearch);
    } else {
      liveEntitiesAndAccounts.bankAccounts.search(bankAccountSearch);
    }
  }, [bankAccountSearch, mode]);
  const showLoadMore = bankAccountSearch === '';
  const isSearchLoading =
    mode === Mode.Sandbox
      ? sandboxEntitiesAndAccounts.bankAccounts.isSearchLoading
      : liveEntitiesAndAccounts.bankAccounts.isSearchLoading;

  const platformLevelPermissions = (useWatch<any>({
    name: getFieldPath('platformLevelPermissions', fieldPathPrefix),
  }) ?? NO_PERMISSIONS) as Partial<KeysCamelCaseDeep<PlatformLevelPermissions>>;
  const [customAccessSelected, setCustomAccessSelected] = useState<boolean>(false);
  const defaultAccess = useMemo(() => {
    if (customAccessSelected) {
      return Access.Custom;
    }
    if (
      Object.keys(platformLevelPermissionsToLabelsMap).every(
        (value) =>
          platformLevelPermissions[value as keyof KeysCamelCase<PlatformLevelPermissions>] === 'default' ||
          platformLevelPermissions[value as keyof KeysCamelCase<PlatformLevelPermissions>] === 'none'
      )
    ) {
      return Access.NoAccess;
    }
    if (
      Object.keys(platformLevelPermissionsToLabelsMap).every(
        (value) => platformLevelPermissions[value as keyof KeysCamelCase<PlatformLevelPermissions>] === 'read'
      )
    ) {
      return Access.ViewOnly;
    }
    if (
      Object.keys(platformLevelPermissionsToLabelsMap).every(
        (value) => platformLevelPermissions[value as keyof KeysCamelCase<PlatformLevelPermissions>] === 'write'
      )
    ) {
      return Access.FullAccess;
    }
    return Access.Custom;
  }, [customAccessSelected, platformLevelPermissions, platformLevelPermissionsToLabelsMap]);
  const showCustomPermissions = defaultAccess === Access.Custom;

  const setDefaultAccess = useCallback(
    (access: Access) => {
      let value: `${CustomRolePermissionLevel}`;
      switch (access) {
        case Access.Custom:
          setCustomAccessSelected(true);
          if (defaultAccess === Access.NoAccess) {
            value = 'read';
            break;
          }
          return;
        case Access.FullAccess:
          setCustomAccessSelected(false);
          value = 'write';
          break;
        case Access.ViewOnly:
          setCustomAccessSelected(false);
          value = 'read';
          break;
        case Access.NoAccess:
          setCustomAccessSelected(false);
          value = 'none';
          break;
        default:
          value = 'default';
          return;
      }
      Object.keys(platformLevelPermissionsToLabelsMap).forEach((permission) => {
        setValue(`${getFieldPath('platformLevelPermissions', fieldPathPrefix)}.${permission}`, value, {
          shouldDirty: true,
          shouldTouch: true,
        });
      });
    },
    [defaultAccess, fieldPathPrefix, platformLevelPermissionsToLabelsMap, setValue]
  );

  return (
    <>
      <div className="px-(--page-margin) py-6">
        {/* Flat secondary panel, on the sidebar's gray-25/950 for color continuity. */}
        <div className="rounded-lg bg-gray-25 shadow-surface-inset dark:bg-gray-950 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)]">
          <div className="grid grid-cols-1 items-start gap-3 px-(--page-margin) py-6 sm:grid-cols-[1fr_minmax(200px,min-content)] sm:gap-6">
            <div className="grid gap-1">
              <SubsectionHeading>Default Account Permissions</SubsectionHeading>
              <p className="m-0 text-xs leading-4 text-muted-foreground">
                Set the default permissions for new and existing accounts
              </p>
            </div>
            <AccessDropdown
              aria-label="Default account permissions"
              className="h-9"
              onChange={setDefaultAccess}
              value={defaultAccess}
            />
          </div>
          <AnimatePresence initial={false}>
            {showCustomPermissions && (
              <motion.div
                key="custom-permissions"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="grid gap-6 px-(--page-margin) pb-6">
                  <Separator />
                  <SubsectionHeading>Transfers</SubsectionHeading>
                  <PermissionToggles
                    control={control}
                    permissionsToLabelsMap={Object.fromEntries(
                      Object.entries(platformLevelPermissionsToLabelsMap).filter(
                        ([permission]) =>
                          permission !== 'bankAccounts' &&
                          permission !== 'entities' &&
                          permission !== 'approveTransfers'
                      )
                    )}
                    fieldPathPrefix={getFieldPath('platformLevelPermissions', fieldPathPrefix)}
                  />
                  <Separator />
                  {showApproveTransfers && (
                    <>
                      <SubsectionHeading>Controls</SubsectionHeading>
                      <FormField<any>
                        id={`${getFieldPath('platformLevelPermissions', fieldPathPrefix)}.approveTransfers`}
                        description="Set the default permissions for approving or rejecting transfers in review."
                        label="Approve Transfers"
                      >
                        {({ value, onChange }) => (
                          <PermissionSelect
                            aria-label="Approve Transfers"
                            value={value === 'write' || value === 'read' ? 'on' : 'off'}
                            options={OptionsOnOff}
                            onChange={(newValue) => onChange(newValue === 'on' ? 'write' : 'none')}
                          />
                        )}
                      </FormField>
                      <Separator />
                    </>
                  )}
                  <SubsectionHeading>Edit Access</SubsectionHeading>
                  <FormField<any>
                    id={`${getFieldPath('platformLevelPermissions', fieldPathPrefix)}.entities`}
                    label="Entities"
                    description="Set the default permissions for new and existing entities."
                  >
                    {({ value, onChange }) => (
                      <PermissionSelect
                        aria-label="Entities"
                        value={value === 'default' ? 'none' : value}
                        options={OptionsEntities}
                        onChange={onChange}
                      />
                    )}
                  </FormField>
                  <FormField<any>
                    id={`${getFieldPath('platformLevelPermissions', fieldPathPrefix)}.bankAccounts`}
                    label="Bank Accounts"
                    description="Set the default permissions for new and existing bank accounts."
                  >
                    {({ value, onChange }) => (
                      <PermissionSelect
                        aria-label="Bank Accounts"
                        value={value === 'default' ? 'none' : value}
                        options={OptionsBankAccounts}
                        onChange={onChange}
                      />
                    )}
                  </FormField>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <div className="grid grid-cols-1 items-start gap-3 px-(--page-margin) pb-3 sm:grid-cols-[1fr_auto] sm:gap-6">
        <div className="grid gap-1">
          <h2 className="m-0 text-base leading-5 font-medium text-foreground">Account Overrides</h2>
          <p className="m-0 text-xs leading-4 text-muted-foreground">
            Override permissions on individual Accounts and Entities.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <InputGroup className="h-8 min-w-[200px] flex-1">
            <InputGroupAddon>
              <Icon.Search className="[--icon-size-x:16px] [--icon-size-y:16px]" />
            </InputGroupAddon>
            <InputGroupInput
              aria-label="Search bank accounts"
              placeholder="Search bank accounts"
              value={bankAccountSearch}
              onChange={(event) => setBankAccountSearch(event.target.value)}
            />
          </InputGroup>
          {currentPlatform?.isLiveEnabled && !forcedMode && (
            <Tabs value={mode} onValueChange={(newMode) => setMode(newMode as Mode)} className="gap-0">
              <TabsList className="h-8!">
                <TabsTrigger value={Mode.Live}>Live</TabsTrigger>
                <TabsTrigger value={Mode.Sandbox}>Sandbox</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>
      </div>
      <Loading isLoading={isSearchLoading} minHeight={false}>
        <AccountOverridesList
          bankAccountSearch={bankAccountSearch}
          bankAccountLevelPermissionsToLabelsMap={bankAccountLevelPermissionsToLabelsMap}
          entitiesAndAccounts={mode === Mode.Sandbox ? sandboxEntitiesAndAccounts : liveEntitiesAndAccounts}
          entityLevelPermissionsToLabelsMap={entityLevelPermissionsToLabelsMap}
          fieldPathPrefix={fieldPathPrefix}
          showLoadMore={showLoadMore}
        />
      </Loading>
    </>
  );
};

const AccountOverridesList: React.FC<{
  bankAccountSearch: string;
  bankAccountLevelPermissionsToLabelsMap: typeof BankAccountLevelPermissionsToLabelsMap;
  entitiesAndAccounts: EntitiesAndAccounts;
  entityLevelPermissionsToLabelsMap: typeof EntityLevelPermissionsToLabelsMap;
  fieldPathPrefix?: string;
  showLoadMore: boolean;
}> = ({
  bankAccountSearch,
  bankAccountLevelPermissionsToLabelsMap,
  entitiesAndAccounts,
  entityLevelPermissionsToLabelsMap,
  fieldPathPrefix,
  showLoadMore,
}) => {
  const { getValues, watch } = useFormContext<any>();
  const { replace: replaceBankAccountOverrides } = useFieldArray({
    name: getFieldPath('bankAccountLevelOverrides', fieldPathPrefix),
  });
  const { replace: replaceEntityOverrides } = useFieldArray({
    name: getFieldPath('entityLevelOverrides', fieldPathPrefix),
  });
  const bankAccountLevelOverrides = watch(getFieldPath('bankAccountLevelOverrides', fieldPathPrefix), []) as Partial<
    KeysCamelCaseDeep<BankAccountLevelOverride>
  >[];
  const entityLevelOverrides = watch(getFieldPath('entityLevelOverrides', fieldPathPrefix), []) as Partial<
    KeysCamelCaseDeep<EntityLevelOverride>
  >[];
  const hasOverrides = useMemo(() => {
    // Only check for overrides on the visible subset of entities and accounts (sandbox or live)
    let nonDefaultEntityOverridesInView = entityLevelOverrides.filter(
      (override: Partial<KeysCamelCaseDeep<EntityLevelOverride>>) =>
        entitiesAndAccounts.entities.data.findIndex((entity) => entity.id === override.entityId) !== -1
    );
    nonDefaultEntityOverridesInView = nonDefaultEntityOverridesInView
      .map((entity: Partial<KeysCamelCaseDeep<EntityLevelOverride>>) => {
        return Object.fromEntries(Object.entries(entity).filter(([k, v]) => k === 'entityId' || v !== 'default'));
      })
      .filter((entity: Record<string, unknown>) => Object.keys(entity).length > 1);
    const nonDefaultBankAccountOverridesInView = bankAccountLevelOverrides
      .filter(
        (override: Partial<KeysCamelCaseDeep<BankAccountLevelOverride>>) =>
          entitiesAndAccounts.bankAccounts.data.findIndex(
            (bankAccount) => bankAccount.id === override.bankAccountId
          ) !== -1
      )
      .map((bankAccount: Partial<KeysCamelCaseDeep<BankAccountLevelOverride>>) => {
        return Object.fromEntries(
          Object.entries(bankAccount).filter(([k, v]) => k === 'bankAccountId' || v !== 'default')
        );
      })
      .filter((bankAccount: Record<string, unknown>) => Object.keys(bankAccount).length > 1);
    return nonDefaultEntityOverridesInView.length + nonDefaultBankAccountOverridesInView.length > 0;
  }, [bankAccountLevelOverrides, entitiesAndAccounts, entityLevelOverrides]);
  const clearOverrides = useCallback(() => {
    // Only clear overrides for the visible subset of entities and accounts (sandbox or live)
    replaceBankAccountOverrides(
      getValues(getFieldPath('bankAccountLevelOverrides', fieldPathPrefix)).filter(
        (override: Partial<KeysCamelCaseDeep<BankAccountLevelOverride>>) =>
          entitiesAndAccounts.bankAccounts.data.findIndex(
            (bankAccount) => bankAccount.id === override.bankAccountId
          ) === -1
      )
    );
    replaceEntityOverrides(
      getValues(getFieldPath('entityLevelOverrides', fieldPathPrefix)).filter(
        (override: Partial<KeysCamelCaseDeep<EntityLevelOverride>>) =>
          entitiesAndAccounts.entities.data.findIndex((entity) => entity.id === override.entityId) === -1
      )
    );
  }, [entitiesAndAccounts, fieldPathPrefix, getValues, replaceBankAccountOverrides, replaceEntityOverrides]);
  const bankAccounts = entitiesAndAccounts.bankAccounts.data.filter((bankAccount) => {
    if (!bankAccountSearch) return true;
    return (
      (bankAccount.description && bankAccount.description.toLowerCase().includes(bankAccountSearch.toLowerCase())) ||
      (bankAccount.displayName && bankAccount.displayName.toLowerCase().includes(bankAccountSearch.toLowerCase()))
    );
  });
  const entities = entitiesAndAccounts.entities.data.filter((entity) => {
    if (!bankAccountSearch) return true;
    return bankAccounts.some((bankAccount) => bankAccount.owners.includes(entity.id as string));
  });
  const rootEntities = entities.filter((entity) => entity.isRoot);
  const nonRootEntities = entities.filter((entity) => !entity.isRoot);
  const entityIdToBankAccountsMap = useMemo(() => {
    const map = new Map<string, BankAccountWithDetails[]>();
    bankAccounts.forEach((bankAccount) => {
      bankAccount.owners.forEach((entityId) => {
        if (!map.has(entityId)) {
          map.set(entityId, []);
        }
        map.get(entityId)?.push(bankAccount);
      });
    });
    return map;
  }, [bankAccounts, entities]);

  return (
    <div className="border-t border-gray-150 dark:border-white/10">
      <ListRow
        className="font-medium text-muted-foreground"
        rightDecoration={
          <AccountActions>
            <span>Access</span>
            {hasOverrides && (
              <Button
                type="button"
                size="xs"
                variant="subtle-primary"
                className="px-0"
                onClick={() => clearOverrides()}
              >
                Clear overrides
                <Icon.Reset className="[--icon-size-x:14px] [--icon-size-y:14px]" />
              </Button>
            )}
          </AccountActions>
        }
      >
        Entities and accounts
      </ListRow>
      {[...rootEntities, ...nonRootEntities].map((entity) => (
        <EntityListSection
          key={entity.id}
          entity={entity}
          bankAccountLevelPermissionsToLabelsMap={bankAccountLevelPermissionsToLabelsMap}
          bankAccounts={entityIdToBankAccountsMap.get(entity.id as string)}
          entityLevelPermissionsToLabelsMap={entityLevelPermissionsToLabelsMap}
          fieldPathPrefix={fieldPathPrefix}
          hasMoreBankAccounts={entitiesAndAccounts.bankAccounts.paginationState[entity.id as string]?.hasMore}
          loadMoreBankAccounts={() => entitiesAndAccounts.bankAccounts.loadMore(entity.id as string)}
          showLoadMore={showLoadMore}
          rootEntity={entity.isRoot}
        />
      ))}
      {entitiesAndAccounts.entities.paginationState.hasMore && (
        <ListRow>
          <LoadMoreButton onClick={() => entitiesAndAccounts.entities.loadMore()}>Load More Entities</LoadMoreButton>
        </ListRow>
      )}
    </div>
  );
};

interface EntityListSectionProps {
  bankAccounts?: BankAccountWithDetails[];
  bankAccountLevelPermissionsToLabelsMap: typeof BankAccountLevelPermissionsToLabelsMap;
  entity: EntityEntity;
  entityLevelPermissionsToLabelsMap: typeof EntityLevelPermissionsToLabelsMap;
  fieldPathPrefix?: string;
  hasMoreBankAccounts?: boolean;
  loadMoreBankAccounts: () => void;
  rootEntity?: boolean;
  showLoadMore?: boolean;
}
const EntityListSection: React.FC<EntityListSectionProps> = ({
  bankAccounts,
  bankAccountLevelPermissionsToLabelsMap,
  entity,
  entityLevelPermissionsToLabelsMap,
  fieldPathPrefix,
  hasMoreBankAccounts,
  loadMoreBankAccounts,
  rootEntity,
  showLoadMore,
}) => {
  const [isOverridesModalOpen, setIsOverridesModalOpen] = useState(false);
  const openOverridesModal = useCallback(() => setTimeout(() => setIsOverridesModalOpen(true), 150), []);
  const closeOverridesModal = useCallback(() => setIsOverridesModalOpen(false), []);
  const { setValue } = useFormContext();

  const platformLevelPermissions = (useWatch<any>({
    name: getFieldPath('platformLevelPermissions', fieldPathPrefix),
  }) ?? NO_PERMISSIONS) as Partial<KeysCamelCaseDeep<PlatformLevelPermissions>>;
  const { replace: replaceBankAccounts } = useFieldArray({
    name: getFieldPath('bankAccountLevelOverrides', fieldPathPrefix),
  });
  const bankAccountLevelOverrides = useWatch<any>({
    defaultValue: [],
    name: getFieldPath('bankAccountLevelOverrides', fieldPathPrefix),
  }) as Partial<KeysCamelCaseDeep<BankAccountLevelOverride>>[];
  const { append: appendEntity, remove: removeEntity } = useFieldArray({
    name: getFieldPath('entityLevelOverrides', fieldPathPrefix),
  });
  const entityLevelOverrides = useWatch<any>({
    defaultValue: [],
    name: getFieldPath('entityLevelOverrides', fieldPathPrefix),
  }) as Partial<KeysCamelCaseDeep<EntityLevelOverride>>[];

  const {
    entityIndex,
    actualPermissions: entityPermissions,
    effectivePermissions: entityEffectivePermissions,
    numOverrides,
  } = useMemo(
    () =>
      getEntityPermissions({
        entityId: entity.id as string,
        entityLevelOverrides,
        permissionsToLabelsMap: entityLevelPermissionsToLabelsMap,
        platformPermissions: platformLevelPermissions,
      }),
    [entity.id, entityLevelOverrides, entityLevelPermissionsToLabelsMap, platformLevelPermissions]
  );

  const setOverridesFromPermissionLevel = useCallback(
    (permissionLevel: `${CustomRolePermissionLevel}`) => {
      if (entityIndex === -1) {
        appendEntity(
          buildEntityOverridesFromPermissionLevel(
            entity.id as string,
            permissionLevel,
            entityLevelPermissionsToLabelsMap
          )
        );
      } else {
        setEntityPermissionsFromPermissionLevel({
          permissionLevel,
          permissionsToLabelsMap: entityLevelPermissionsToLabelsMap,
          keyPrefix: `${getFieldPath('entityLevelOverrides', fieldPathPrefix)}.${entityIndex}.`,
          setter: setValue as (key: string, value: string) => void,
        });
      }
    },
    [entity.id, entityIndex, appendEntity, entityLevelPermissionsToLabelsMap, fieldPathPrefix, setValue]
  );

  const setOverridesFromPermissions = useCallback(
    (permissions: Permissions) => {
      if (entityIndex === -1) {
        appendEntity(buildEntityOverridesFromPermissions(entity.id as string, permissions));
      } else {
        setEntityPermissionsFromPermissions({
          permissions,
          keyPrefix: `${getFieldPath('entityLevelOverrides', fieldPathPrefix)}.${entityIndex}.`,
          setter: setValue as (key: string, value: string) => void,
        });
      }
    },
    [entity.id, entityIndex, appendEntity, fieldPathPrefix, setValue]
  );

  // The current (persisted in the form) access level for this entity
  const entityAccessLevel = useMemo(() => {
    if (entityIndex === -1) {
      return Access.Default;
    }
    return getAccessLevel(entityPermissions);
  }, [entityIndex, entityPermissions]);
  // The access level to show in the UI
  const visibleAccessLevel = isOverridesModalOpen ? Access.Custom : entityAccessLevel;

  const setEntityAccessLevel = useCallback(
    (access: Access) => {
      switch (access) {
        case Access.Custom:
          openOverridesModal();
          return;
        case Access.Default:
          if (entityIndex !== -1) {
            removeEntity(entityIndex);
          }
          return;
        case Access.FullAccess:
          setOverridesFromPermissionLevel('write');
          return;
        case Access.ViewOnly:
          setOverridesFromPermissionLevel('read');
          return;
        case Access.NoAccess:
          setOverridesFromPermissionLevel('none');
          // Remove all bank account overrides for this entity
          replaceBankAccounts(
            bankAccountLevelOverrides.filter(
              (override) => !bankAccounts?.some((bankAccount) => bankAccount.id === override.bankAccountId)
            )
          );
          return;
      }
    },
    [bankAccountLevelOverrides, replaceBankAccounts, setOverridesFromPermissionLevel]
  );

  return (
    <>
      <CustomOverridesModal
        breadcrumbs={[entity.name as string]}
        currentPermissions={
          entityAccessLevel === Access.Custom
            ? entityPermissions
            : Object.fromEntries(Object.keys(entityLevelPermissionsToLabelsMap).map((key) => [key, 'default']))
        }
        onClose={closeOverridesModal}
        onSave={(permissions) => {
          setOverridesFromPermissions(permissions);
          closeOverridesModal();
        }}
        open={isOverridesModalOpen}
        parentPermissions={platformLevelPermissions as Permissions}
        permissionsToLabelsMap={entityLevelPermissionsToLabelsMap}
      />
      <ListRow
        className="bg-gray-25 font-medium dark:bg-white/[0.02]"
        icon={
          <Icon.Folder
            filled={!!rootEntity}
            flat
            className="shrink-0 text-gray-400 [--icon-size-x:20px] [--icon-size-y:20px] dark:text-gray-500"
          />
        }
        rightDecoration={
          <>
            {entityAccessLevel === Access.Custom && numOverrides > 0 && <OverridesBadge count={numOverrides} />}
            <AccountActions>
              <AccessDropdown
                aria-label={`Access for ${entity.name}`}
                variant="card"
                onChange={setEntityAccessLevel}
                value={visibleAccessLevel}
                showDefault
              />
              {visibleAccessLevel === Access.Custom && (
                <Button type="button" size="sm" variant="secondary" onClick={openOverridesModal}>
                  Edit
                </Button>
              )}
            </AccountActions>
          </>
        }
      >
        <span className="truncate">{entity.name as string}</span>
      </ListRow>
      {bankAccounts?.map((bankAccount) => (
        <BankAccountListRow
          key={bankAccount.id}
          bankAccount={bankAccount}
          bankAccountLevelPermissionsToLabelsMap={bankAccountLevelPermissionsToLabelsMap}
          fieldPathPrefix={fieldPathPrefix}
          parentName={entity.name as string}
          parentOverrides={entityLevelOverrides}
          parentPermissions={entityEffectivePermissions}
        />
      ))}
      {hasMoreBankAccounts && showLoadMore && (
        <ListRow depth={1}>
          <LoadMoreButton onClick={() => loadMoreBankAccounts()}>Load More Accounts</LoadMoreButton>
        </ListRow>
      )}
    </>
  );
};

interface BankAccountListRowProps {
  bankAccount: BankAccountWithDetails;
  bankAccountLevelPermissionsToLabelsMap: typeof BankAccountLevelPermissionsToLabelsMap;
  fieldPathPrefix?: string;
  parentName: string;
  parentOverrides: Partial<KeysCamelCaseDeep<EntityLevelOverride>>[];
  parentPermissions: Permissions;
}
const BankAccountListRow: React.FC<BankAccountListRowProps> = ({
  bankAccount,
  bankAccountLevelPermissionsToLabelsMap,
  fieldPathPrefix,
  parentName,
  parentOverrides,
  parentPermissions,
}) => {
  const [isOverridesModalOpen, setIsOverridesModalOpen] = useState<boolean>(false);
  const openOverridesModal = useCallback(() => setTimeout(() => setIsOverridesModalOpen(true), 150), []);
  const closeOverridesModal = useCallback(() => setIsOverridesModalOpen(false), []);
  const { setValue } = useFormContext();

  const bankAccountLevelOverrides = useWatch<any>({
    defaultValue: [],
    name: getFieldPath('bankAccountLevelOverrides', fieldPathPrefix),
  }) as Partial<KeysCamelCaseDeep<BankAccountLevelOverride>>[];
  const { append: appendBankAccount, remove: removeBankAccount } = useFieldArray({
    name: getFieldPath('bankAccountLevelOverrides', fieldPathPrefix),
  });

  const {
    bankAccountIndex,
    actualPermissions: bankAccountPermissions,
    numOverrides,
  } = useMemo(
    () =>
      getBankAccountPermissions({
        bankAccountId: bankAccount.id,
        bankAccountLevelOverrides,
        permissionsToLabelsMap: bankAccountLevelPermissionsToLabelsMap,
      }),
    [bankAccount.id, bankAccountLevelOverrides, bankAccountLevelPermissionsToLabelsMap]
  );

  const setOverridesFromPermissionLevel = useCallback(
    (permissionLevel: `${CustomRolePermissionLevel}`) => {
      if (bankAccountIndex === -1) {
        appendBankAccount(
          buildBankAccountOverridesFromPermissionLevel(
            bankAccount.id,
            permissionLevel,
            bankAccountLevelPermissionsToLabelsMap
          )
        );
      } else {
        setBankAccountPermissionsFromPermissionLevel({
          permissionLevel,
          permissionsToLabelsMap: bankAccountLevelPermissionsToLabelsMap,
          keyPrefix: `${getFieldPath('bankAccountLevelOverrides', fieldPathPrefix)}.${bankAccountIndex}.`,
          setter: setValue as (key: string, value: string) => void,
        });
      }
    },
    [
      bankAccount.id,
      bankAccountIndex,
      appendBankAccount,
      bankAccountLevelPermissionsToLabelsMap,
      fieldPathPrefix,
      setValue,
    ]
  );

  const setOverridesFromPermissions = useCallback(
    (permissions: Permissions) => {
      if (bankAccountIndex === -1) {
        appendBankAccount(buildBankAccountOverridesFromPermissions(bankAccount.id, permissions));
      } else {
        setBankAccountPermissionsFromPermissions({
          permissions,
          keyPrefix: `${getFieldPath('bankAccountLevelOverrides', fieldPathPrefix)}.${bankAccountIndex}.`,
          setter: setValue as (key: string, value: string) => void,
        });
      }
    },
    [bankAccount.id, bankAccountIndex, appendBankAccount, fieldPathPrefix, setValue]
  );

  // The current (persisted in the form) access level for this bank account
  const bankAccountAccessLevel = useMemo(() => {
    if (bankAccountIndex === -1) {
      return Access.Default;
    }
    return getAccessLevel(bankAccountPermissions);
  }, [bankAccountIndex, bankAccountPermissions]);
  // The access level to show in the UI
  const visibleAccessLevel = isOverridesModalOpen ? Access.Custom : bankAccountAccessLevel;
  const parentAccessLevel = useMemo(() => getAccessLevel(parentPermissions), [parentPermissions]);

  const setBankAccountAccessLevel = useCallback(
    (access: Access) => {
      switch (access) {
        case Access.Custom:
          openOverridesModal();
          return;
        case Access.Default:
          if (bankAccountIndex !== -1) {
            removeBankAccount(bankAccountIndex);
          }
          return;
        case Access.FullAccess:
          setOverridesFromPermissionLevel('write');
          return;
        case Access.ViewOnly:
          setOverridesFromPermissionLevel('read');
          return;
        case Access.NoAccess:
          setOverridesFromPermissionLevel('none');
          return;
      }
    },
    [setOverridesFromPermissionLevel, removeBankAccount]
  );

  const showAccessDropdown = parentOverrides.length === 0 || parentAccessLevel !== Access.NoAccess;
  const bankAccountName = bankAccount.displayName ?? bankAccount.description ?? 'Unnamed';

  return (
    <>
      <CustomOverridesModal
        breadcrumbs={[parentName, bankAccountName]}
        currentPermissions={
          bankAccountAccessLevel === Access.Custom
            ? bankAccountPermissions
            : Object.fromEntries(Object.keys(bankAccountLevelPermissionsToLabelsMap).map((key) => [key, 'default']))
        }
        onClose={closeOverridesModal}
        onSave={(permissions) => {
          setOverridesFromPermissions(permissions);
          closeOverridesModal();
        }}
        open={isOverridesModalOpen}
        parentPermissions={parentPermissions}
        permissionsToLabelsMap={bankAccountLevelPermissionsToLabelsMap}
      />
      <ListRow
        depth={1}
        rightDecoration={
          showAccessDropdown && (
            <>
              {bankAccountAccessLevel === Access.Custom && numOverrides > 0 && <OverridesBadge count={numOverrides} />}
              <AccountActions>
                <AccessDropdown
                  aria-label={`Access for ${bankAccountName}`}
                  variant="card"
                  onChange={setBankAccountAccessLevel}
                  value={visibleAccessLevel}
                  showDefault
                />
                {visibleAccessLevel === Access.Custom && (
                  <Button type="button" size="sm" variant="muted" onClick={openOverridesModal}>
                    Edit
                  </Button>
                )}
              </AccountActions>
            </>
          )
        }
      >
        <span className="truncate text-foreground">{bankAccountName}</span>
        {bankAccount.displayName && bankAccount.description && (
          <span className="truncate text-xs text-muted-foreground">({bankAccount.description})</span>
        )}
      </ListRow>
    </>
  );
};
